import { useParams } from "react-router";
import { useFoodpilotContext } from "src/context/FoodpilotContext.tsx";
import { usePackagingContext } from "src/pages/Packaging/PackagingDetails/PackagingContext.tsx";
import { Loading } from "src/components/utils/Loading.tsx";
import { ScoreDetailContextProvider } from "src/context/ScoreDetail/provider.tsx";
import { URLHandler } from "src/services/URLHandler";
import { useEntityScores } from "src/context/Scores/useEntityScores";
import { OlapParamsService } from "src/context/OlapScores/OlapParamsService";
import { useOlapScoresParams } from "src/context/OlapScores/useOlapScoresParams";
import { useGetPostScores } from "src/api/client-api/olapScores/useGetPostScores";
import { Dimensions } from "src/context/OlapScores/useDimensions";
import { NoScoresAvailableMessage } from "src/components/Message/NoScoresAvailableMessage";
import { ScoreQueryError } from "src/components/ScoreState/ScoreQueryError";
import { getRemoteErrorStatus } from "src/api/remoteCollectionState";
import { useIsProductUxV2 } from "src/hooks/useIsProductUxV2";

type PackagingScoreContextProviderProps = {
    children: ReactNode | ReactNode[];
};
const PackagingScoreContextProvider = (props: PackagingScoreContextProviderProps) => {
    const { children } = props;
    const isProductUxV2 = useIsProductUxV2();
    const appearance = isProductUxV2 ? "product-v2" : "legacy";

    const { scoreId: scoreIdParam } = useParams<{ scoreId: string }>();
    const scoreId = Number(scoreIdParam);

    const scoreDetailDimensions: Dimensions = [
        [["headings"], ["subheadings"], ["properties"], ["subproperties"], ["multivalues"]],
        [["ssq_versions"], ["headings"], ["subheadings"], ["properties"], ["subproperties"], ["multivalues"]],
    ];
    const initialDimensions = OlapParamsService.getInitialDimensions(scoreDetailDimensions);

    const { selectedCompany } = useFoodpilotContext();
    const {
        packaging,
        ssqVersion,
        post,
        isCampaignError,
        campaignError,
        retryCampaigns,
        isPostUnavailable,
        isPostIdle,
        isPostLoading,
        isPostSuccess,
        isSsqVersionLoading,
        isSsqVersionError,
        ssqVersionError,
        refetchSsqVersion,
        isPostError,
        postError,
        refetchPost,
    } = usePackagingContext();
    const { scoresWithVariations, selectedVariations, getEntityScore } = useEntityScores();
    const score = getEntityScore(scoreId);

    const scoresParams = useOlapScoresParams({
        scores: {
            ids: [scoreId],
            dimensions: initialDimensions,
            variations: selectedVariations.query,
        },
        prevScores: true,
    });

    const {
        data: scores,
        error: scoreQueryError,
        isError: isScoreQueryError,
        isLoading,
        isRefetching,
        refetch: retryScoreQuery,
    } = useGetPostScores({ postId: post?.id, scoresQuery: scoresParams.query });

    if (isCampaignError) {
        return (
            <ScoreQueryError context="details" onRetry={retryCampaigns} status={getRemoteErrorStatus(campaignError)} />
        );
    }

    if (isPostError) {
        return <ScoreQueryError context="details" onRetry={refetchPost} status={getRemoteErrorStatus(postError)} />;
    }

    if (isPostUnavailable) {
        return <NoScoresAvailableMessage appearance={appearance} />;
    }

    if (!selectedCompany || packaging === undefined || isPostLoading || isSsqVersionLoading) {
        return <Loading appearance={appearance} />;
    }

    if (isPostIdle || !isPostSuccess) {
        return <Loading appearance={appearance} />;
    }

    if (post === undefined) {
        return <NoScoresAvailableMessage appearance={appearance} />;
    }

    if (post.ssqVersionId !== undefined && isSsqVersionError) {
        return (
            <ScoreQueryError
                context="details"
                onRetry={refetchSsqVersion}
                status={getRemoteErrorStatus(ssqVersionError)}
            />
        );
    }

    if (score === undefined) {
        return <ScoreQueryError context="details" status="not-found" />;
    }

    if (isScoreQueryError) {
        return (
            <ScoreQueryError
                context="details"
                onRetry={retryScoreQuery}
                status={getRemoteErrorStatus(scoreQueryError)}
            />
        );
    }

    if (scores !== undefined && scores.olapScores[scoreId] === undefined) {
        return <NoScoresAvailableMessage appearance={appearance} />;
    }

    const scoreWithVariations = scoresWithVariations?.filter(
        (scoreWithVariation) => scoreWithVariation.scoreId === scoreId,
    );

    return (
        <ScoreDetailContextProvider
            score={score}
            olapScores={scores?.olapScores[scoreId]}
            scoresParams={scoresParams}
            scoreDetailDimensions={scoreDetailDimensions}
            ignoredDrillDownDimensions={["multivalues"]}
            olapLocalizedLabelContext={{ ssqVersion }}
            fetchPostForAnswers={ssqVersion === undefined ? undefined : refetchPost}
            selectedVariations={selectedVariations}
            scoreWithVariations={scoreWithVariations}
            ssqVersion={ssqVersion}
            isLoading={isLoading || isRefetching || isSsqVersionLoading}
            prevCampaignLabel={undefined}
            goBackLink={URLHandler.packaging.scores(selectedCompany?.id, packaging.id)}
            tabConfig={{
                distribution: {
                    link: (scoreId) =>
                        URLHandler.packaging.scoreDistribution(selectedCompany?.id, packaging.id, scoreId.toString()),
                },
            }}
        >
            {children}
        </ScoreDetailContextProvider>
    );
};

export default PackagingScoreContextProvider;
